import type { SimulationResult } from '@rcs/physics';
import type { Vehicle } from '@rcs/data-schema';

/** 評価レベル（FR-SAF-005）：情報・注意・警告・続行不可の 4 段階。 */
export type WarningLevel = 'info' | 'caution' | 'warning' | 'blocking';

/** 評価レベルの重さの順（軽い → 重い）。 */
export const WARNING_LEVELS: readonly WarningLevel[] = ['info', 'caution', 'warning', 'blocking'];

/** 評価の種類。 */
export type WarningKind =
  | 'stall'
  | 'speed-high'
  | 'vertical-g-high'
  | 'vertical-g-low'
  | 'lateral-g'
  | 'numerical-error'
  | 'finished';

/** 走行の評価（警告・情報）。 */
export interface RideWarning {
  /** 一意の ID */
  readonly id: string;
  readonly level: WarningLevel;
  readonly kind: WarningKind;
  /** 区間の始まり・終わりの弧長 [m]（地点なら同じ値） */
  readonly startS: number;
  readonly endS: number;
  /** 区間の始まり・終わりの時刻 [s] */
  readonly startTime: number;
  readonly endTime: number;
  /** 区間内の最も大きな値（G の超過。速度の超過は km/h）。失速・情報では `undefined` */
  readonly peak?: number;
  /** 超えた上限（G。速度は km/h）。失速・情報では `undefined` */
  readonly limit?: number;
}

/** 評価に使う車両の限界値。 */
export type VehicleLimits = Vehicle['limits'];

/** 上限を超えた度合いが、この倍率以上なら「警告」。それ未満は「注意」。 */
export const WARNING_RATIO = 1.25;
/** 上限を超えた度合いが、この倍率以上なら「続行不可」。 */
export const BLOCKING_RATIO = 1.75;

/**
 * 上限 `limit` に対する超過の度合い（1 = ちょうど上限）。
 * 上限が 0 に近いとき（下向き G の許容なし）も暴走しないよう、分母を 1 以上にする。
 */
export function exceedRatio(value: number, limit: number): number {
  return 1 + (Math.abs(value) - Math.abs(limit)) / Math.max(Math.abs(limit), 1);
}

/** 超過の度合いから、評価レベルを決める。 */
export function levelForRatio(ratio: number): WarningLevel {
  if (ratio >= BLOCKING_RATIO) return 'blocking';
  if (ratio >= WARNING_RATIO) return 'warning';
  return 'caution';
}

interface Run {
  startIndex: number;
  endIndex: number;
  peak: number;
}

/** 値が上限を超えているフレームの連続区間を求める。 */
function findRuns(values: readonly number[], exceeds: (value: number) => boolean): Run[] {
  const runs: Run[] = [];
  let current: Run | undefined;
  values.forEach((value, index) => {
    if (exceeds(value)) {
      if (current === undefined) current = { startIndex: index, endIndex: index, peak: value };
      current.endIndex = index;
      if (Math.abs(value) > Math.abs(current.peak)) current.peak = value;
    } else if (current !== undefined) {
      runs.push(current);
      current = undefined;
    }
  });
  if (current !== undefined) runs.push(current);
  return runs;
}

/**
 * 走行の結果を、車両の限界値と照らして評価する（FR-SAF-001, 002, 005）。
 *
 * - 失速（stall）：警告。地点（弧長・時刻）を示す
 * - 速度が車両の最高速度を超えた区間、上下 G が上限・下限を超えた区間、横 G が上限を超えた区間：
 *   超過の度合いで注意・警告・続行不可
 * - 数値の異常で止まった場合：続行不可
 * - 最後まで走り切った場合：情報
 *
 * 評価はゲーム内の参考指標であり、実在設備の安全性を示すものではない（FR-SAF-007）。
 */
export function evaluateRide(result: SimulationResult, limits: VehicleLimits): RideWarning[] {
  const warnings: RideWarning[] = [];
  const frames = result.frames;

  result.events.forEach((event, index) => {
    if (event.type === 'stall') {
      warnings.push({
        id: `stall-${String(index)}`,
        level: 'warning',
        kind: 'stall',
        startS: event.s,
        endS: event.s,
        startTime: event.time,
        endTime: event.time,
      });
    }
    if (event.type === 'numericalError') {
      warnings.push({
        id: `numerical-error-${String(index)}`,
        level: 'blocking',
        kind: 'numerical-error',
        startS: event.s,
        endS: event.s,
        startTime: event.time,
        endTime: event.time,
      });
    }
  });

  const addRuns = (
    kind: WarningKind,
    values: readonly number[],
    limit: number,
    exceeds: (value: number) => boolean,
  ): void => {
    findRuns(values, exceeds).forEach((run, index) => {
      const start = frames[run.startIndex];
      const end = frames[run.endIndex];
      if (!start || !end) return;
      warnings.push({
        id: `${kind}-${String(index)}`,
        level: levelForRatio(exceedRatio(run.peak, limit)),
        kind,
        startS: start.s,
        endS: end.s,
        startTime: start.time,
        endTime: end.time,
        peak: run.peak,
        limit,
      });
    });
  };

  // 車両の最高速度（km/h で比べる）
  addRuns(
    'speed-high',
    frames.map((f) => f.speed * 3.6),
    limits.maxSpeed * 3.6,
    (v) => v > limits.maxSpeed * 3.6,
  );
  addRuns(
    'vertical-g-high',
    frames.map((f) => f.gForce.vertical),
    limits.maxPositiveG,
    (v) => v > limits.maxPositiveG,
  );
  addRuns(
    'vertical-g-low',
    frames.map((f) => f.gForce.vertical),
    limits.maxNegativeG,
    (v) => v < limits.maxNegativeG,
  );
  addRuns(
    'lateral-g',
    frames.map((f) => f.gForce.lateral),
    limits.maxLateralG,
    (v) => Math.abs(v) > limits.maxLateralG,
  );

  const last = frames[frames.length - 1];
  const finished = result.meta.termination === 'trackEnd' || result.meta.termination === 'rest';
  if (finished && last && !warnings.some((w) => w.level === 'blocking')) {
    warnings.push({
      id: 'finished',
      level: 'info',
      kind: 'finished',
      startS: last.s,
      endS: last.s,
      startTime: last.time,
      endTime: last.time,
    });
  }

  return warnings.sort((a, b) => a.startTime - b.startTime || a.id.localeCompare(b.id));
}

/** 警告のうち、最も重いレベル。警告がなければ `undefined`。 */
export function worstLevel(warnings: readonly RideWarning[]): WarningLevel | undefined {
  let worst: WarningLevel | undefined;
  for (const warning of warnings) {
    if (
      worst === undefined ||
      WARNING_LEVELS.indexOf(warning.level) > WARNING_LEVELS.indexOf(worst)
    ) {
      worst = warning.level;
    }
  }
  return worst;
}
